import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Send, Loader2, Mail, ShieldCheck, Copy, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /message-sirchewyyy — public form for anyone to reach the
 * Architect. Returns a claim URL the sender bookmarks to check
 * for a reply (including a temp password if it's a reset).
 */
export function MessageSirchewyyy() {
  const [tab, setTab] = useState("send");
  const [form, setForm] = useState({
    sender_email: "",
    sender_name: "",
    subject: "",
    body: "",
    category: "password_reset",
    passphrase: "",
  });
  const [recover, setRecover] = useState({ email: "", passphrase: "" });
  const [state, setState] = useState({ submitting: false, result: null, error: null });
  const [threads, setThreads] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setState({ submitting: true, result: null, error: null });
    try {
      const r = await axios.post(`${API}/messages/to-architect`, form);
      setState({ submitting: false, result: r.data, error: null });
    } catch (err) {
      setState({
        submitting: false,
        result: null,
        error: err.response?.data?.detail || err.message,
      });
    }
  };

  const doRecover = async (e) => {
    e.preventDefault();
    setState({ submitting: true, result: null, error: null });
    setThreads(null);
    try {
      const r = await axios.post(`${API}/messages/lookup`, {
        sender_email: recover.email,
        passphrase: recover.passphrase,
      });
      setThreads(r.data.threads);
      setState({ submitting: false, result: null, error: null });
    } catch (err) {
      setState({
        submitting: false,
        result: null,
        error: err.response?.data?.detail || err.message,
      });
    }
  };

  if (state.result) {
    const claimUrl = `${window.location.origin}${state.result.claim_url}`;
    return (
      <div className="min-h-screen bg-[#07070a] text-zinc-100 flex items-center justify-center p-6">
        <div className="max-w-lg w-full border border-emerald-500/40 bg-emerald-500/5 p-8">
          <div className="inline-flex items-center gap-2 text-emerald-400 mb-4">
            <Check className="h-4 w-4" />
            <div className="text-[10px] uppercase tracking-[0.3em]">Message sent</div>
          </div>
          <h2 className="text-xl font-heading mb-4">Bookmark this URL</h2>
          <p className="text-sm text-zinc-400 mb-4">
            When Sirchewyyy replies, the reply appears at the URL below. The link
            expires in 30 days. Keep it safe — anyone with the link can see the
            reply.
          </p>
          <div className="border border-zinc-800 bg-[#0b0b0d] p-4 font-mono text-xs text-emerald-400 break-all mb-4"
               data-testid="claim-url">
            {claimUrl}
          </div>
          <button
            onClick={() => navigator.clipboard.writeText(claimUrl)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 text-xs uppercase tracking-[0.3em]"
            data-testid="copy-claim-btn"
          >
            <Copy className="h-3 w-3" />
            Copy URL
          </button>
          <div className="mt-6 text-[10px] text-zinc-600">
            <Link to={state.result.claim_url} className="text-emerald-500/70 hover:text-emerald-400">
              → Open your inbox now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07070a] text-zinc-100 py-16 px-6">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 px-4 py-1.5 text-[10px] uppercase tracking-[0.35em] text-amber-300 mb-6">
            <Mail className="h-3 w-3" />
            Message Sirchewyyy
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading text-zinc-50">Locked out?</h1>
          <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
            Drop a message here — password reset, support, anything else. When
            Sirchewyyy sees it, they'll reply on the URL you get back.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 mb-6 border border-zinc-800">
          <button
            type="button"
            onClick={() => { setTab("send"); setThreads(null); setState({...state, error: null}); }}
            className={`flex-1 py-3 text-[10px] uppercase tracking-[0.3em] ${tab === "send" ? "bg-emerald-500 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
            data-testid="tab-send"
          >Send message</button>
          <button
            type="button"
            onClick={() => { setTab("recover"); setState({...state, error: null}); }}
            className={`flex-1 py-3 text-[10px] uppercase tracking-[0.3em] ${tab === "recover" ? "bg-emerald-500 text-zinc-950" : "text-zinc-400 hover:text-zinc-100"}`}
            data-testid="tab-recover"
          >Recover thread</button>
        </div>

        <form onSubmit={submit} className={`space-y-5 ${tab === "send" ? "" : "hidden"}`} data-testid="message-form">
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Your email *</label>
            <input
              type="email"
              required
              value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              data-testid="sender-email"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Your name (optional)</label>
            <input
              type="text"
              value={form.sender_name}
              maxLength={80}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              data-testid="sender-name"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Subject (optional)</label>
            <input
              type="text"
              value={form.subject}
              maxLength={120}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              data-testid="subject"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              data-testid="category"
            >
              <option value="password_reset">Password reset</option>
              <option value="support">Support</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Message *</label>
            <textarea
              required
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={6}
              maxLength={2000}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              data-testid="message-body"
              placeholder="Explain what you need. If this is a password reset, mention the email you originally signed up with."
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-2">
              Recovery passphrase * <span className="text-zinc-600">(4+ chars, so you can find this thread later)</span>
            </label>
            <input
              type="text"
              required
              minLength={4}
              value={form.passphrase}
              onChange={(e) => setForm({ ...form, passphrase: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              data-testid="passphrase"
              placeholder="e.g. purple-fish-42"
            />
          </div>
          <button
            type="submit"
            disabled={state.submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-xs uppercase tracking-[0.3em]"
            data-testid="send-btn"
          >
            {state.submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {state.submitting ? "Sending" : "Send to Sirchewyyy"}
          </button>
          {state.error && (
            <div className="text-xs text-rose-400" data-testid="error">{state.error}</div>
          )}
        </form>

        {/* Recover thread panel */}
        <form onSubmit={doRecover} className={`space-y-5 ${tab === "recover" ? "" : "hidden"}`} data-testid="recover-form">
          <p className="text-xs text-zinc-500 leading-relaxed">
            Lost the URL? Enter the email and passphrase you used when you sent your message,
            and we'll show you the thread(s).
          </p>
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Your email *</label>
            <input
              type="email"
              required
              value={recover.email}
              onChange={(e) => setRecover({ ...recover, email: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              data-testid="recover-email"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-2">Passphrase *</label>
            <input
              type="text"
              required
              minLength={4}
              value={recover.passphrase}
              onChange={(e) => setRecover({ ...recover, passphrase: e.target.value })}
              className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 focus:outline-none text-sm p-3 font-body text-zinc-100"
              data-testid="recover-passphrase"
            />
          </div>
          <button
            type="submit"
            disabled={state.submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 disabled:opacity-50 text-xs uppercase tracking-[0.3em]"
            data-testid="recover-btn"
          >
            {state.submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {state.submitting ? "Looking up" : "Find my threads"}
          </button>
          {threads && threads.length > 0 && (
            <div className="mt-6 space-y-2" data-testid="recovered-threads">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                Found {threads.length} thread(s)
              </div>
              {threads.map((t) => (
                <Link
                  key={t.message_id}
                  to={t.claim_url}
                  className="block border border-zinc-800 hover:border-emerald-500 bg-[#0b0b0d] p-4"
                >
                  <div className="text-xs text-zinc-100">{t.subject || "(no subject)"}</div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    {new Date(t.created_at).toLocaleString()} · status: {t.status}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}


/**
 * /inbox/{claim_token} — public thread view. Sender polls here for
 * the Architect's reply, including any temp password.
 */
export function InboxThread() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, thread: null, error: null });
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/messages/thread/${token}`);
      setState({ loading: false, thread: r.data, error: null });
    } catch (e) {
      setState({
        loading: false,
        thread: null,
        error: e.response?.data?.detail || e.message,
      });
    }
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [token]);

  if (state.loading) return <div className="min-h-screen bg-[#07070a] text-zinc-500 p-8">Loading thread…</div>;
  if (state.error) {
    return (
      <div className="min-h-screen bg-[#07070a] text-zinc-100 p-8">
        <div className="max-w-lg mx-auto border border-rose-500/40 bg-rose-500/5 p-6">
          <div className="text-rose-400 text-sm mb-2">Thread unavailable</div>
          <div className="text-zinc-400 text-xs">{state.error}</div>
        </div>
      </div>
    );
  }

  const t = state.thread;
  const reply = t?.reply;
  const tempPassword = reply?.temp_password;

  return (
    <div className="min-h-screen bg-[#07070a] text-zinc-100 p-8">
      <div className="max-w-lg mx-auto space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/5 px-4 py-1.5 text-[10px] uppercase tracking-[0.35em] text-amber-300">
            Your thread with Sirchewyyy
          </div>
          <div className="mt-3 text-xs text-zinc-500">Status: {t?.status}</div>
        </div>

        {/* Original message */}
        <div className="border border-zinc-800 bg-[#0b0b0d] p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">You wrote</div>
          <div className="text-xs text-zinc-400 mb-3">{new Date(t?.created_at).toLocaleString()}</div>
          {t?.subject && <div className="font-heading text-zinc-100 mb-2">{t.subject}</div>}
          <div className="text-sm text-zinc-300 whitespace-pre-wrap">{t?.body}</div>
        </div>

        {/* Reply */}
        {reply ? (
          <div className="border border-emerald-500/40 bg-emerald-500/[0.05] p-5" data-testid="architect-reply">
            <div className="inline-flex items-center gap-2 text-emerald-400 mb-2">
              <ShieldCheck className="h-3 w-3" />
              <div className="text-[10px] uppercase tracking-[0.3em]">Sirchewyyy replied</div>
            </div>
            <div className="text-xs text-zinc-500 mb-3">{new Date(reply.at).toLocaleString()}</div>
            <div className="text-sm text-zinc-100 whitespace-pre-wrap mb-4">{reply.body}</div>

            {tempPassword && (
              <div className="border border-amber-500/40 bg-amber-500/5 p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
                  Temporary password (use once, then change immediately)
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-lg text-emerald-400 break-all"
                        data-testid="temp-password">{tempPassword}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(tempPassword); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className="p-2 border border-amber-500/30 text-amber-300 hover:bg-amber-500 hover:text-zinc-950"
                    data-testid="copy-temp-pw-btn"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </button>
                </div>
                <div className="text-[10px] text-zinc-500 mt-3">
                  Sign in with this password at /signin, then go to Settings → Password to change it.
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-zinc-800 bg-[#0b0b0d] p-5 text-center text-xs text-zinc-500">
            Waiting for Sirchewyyy's reply. This page auto-refreshes every 15 seconds.
          </div>
        )}
      </div>
    </div>
  );
}
